// src/hooks/useIGV.ts
import { useEffect, useRef, useState } from 'react';

import debounce from 'debounce';

import { useOptionalExperimentDetailViewContext } from '@contexts/ExperimentDetailViewContext';

import IGVBrowser from '@models/igv/IGVBrowser';
import IGVBrowserConfig from '@models/igv/IGVBrowserConfig';
import IGVModule from '@models/igv/IGVModule';
import Plot from '@models/Plot';

import Logger from '@util/Logger';

const logger = Logger.make('useIGV');

type Props = {
    config: IGVBrowserConfig;
    plot?: Plot | null;
    instanceId?: string;
};

/**
 * A hook that:
 * - Draws IGV exactly once on mount.
 * - On subsequent config changes, calls loadSessionObject(...) exactly once per change.
 * - Never “double‐draws” (no second spinner) on modal open.
 */
const useIGV = ({ config, plot, instanceId }: Props) => {
    // 1) Container div ref
    const igvRef = useRef<HTMLDivElement>(null);

    // 2) Build a stable “instanceKey” (plot.uuid::instanceId or plot.uuid::randomUUID())
    const plotId = plot?.uuid ?? '';
    const generatedRef = useRef<string | null>(null);
    if (!generatedRef.current) {
        generatedRef.current = instanceId ? `${plotId}::${instanceId}` : `${plotId}::${crypto.randomUUID()}`;
    }
    const instanceKey = generatedRef.current;

    // 3) State for the dynamically imported IGV module
    const [igvModule, setIgvModule] = useState<IGVModule | null>(null);
    // 4) State for the IGVBrowser instance
    const [igvBrowser, setIgvBrowser] = useState<IGVBrowser | null>(null);

    // 5) Shared context if you want to store the browser by key
    const experimentContext = useOptionalExperimentDetailViewContext();

    // 6) Keep track of last container size so we only call visibilityChange when needed
    const lastSizeRef = useRef<{ width: number; height: number } | null>(null);

    // 7) Keep refs up to date for our callbacks
    const igvModuleRef = useRef<IGVModule | null>(igvModule);
    const igvBrowserRef = useRef<IGVBrowser | null>(igvBrowser);

    useEffect(() => {
        igvModuleRef.current = igvModule;
    }, [igvModule]);

    useEffect(() => {
        igvBrowserRef.current = igvBrowser;
    }, [igvBrowser]);

    /**
     * Lazy‐load IGV.js via dynamic import. Once loaded, store it in state.
     */
    const loadIGV = async (): Promise<IGVModule | undefined> => {
        if (igvModule) {
            return igvModule;
        }
        if (typeof window === 'undefined') {
            return;
        }
        const container = igvRef.current;
        if (!container) {
            return;
        }
        container.innerHTML = 'Loading IGV browser…';
        const igvLib = (await import('igv/dist/igv.esm.min.js')) as unknown as IGVModule;
        setIgvModule(igvLib);
        return igvLib;
    };

    /**
     * Called exactly once on mount: clears the <div> and does createBrowser(...).
     */
    const drawIGV = async (container: HTMLDivElement, config: IGVBrowserConfig) => {
        const igvLib = await loadIGV();
        if (!igvLib) {
            logger.log('No IGV module was loaded');
            return;
        }

        const realIGV = (igvLib as any).default ? (igvLib as any).default : igvLib;

        container.innerHTML = '';
        const browser: IGVBrowser = await realIGV.createBrowser(container, {
            ...config,
        });

        if (plotId) {
            experimentContext?.setIGVBrowserByPlotId?.(plotId, browser);
        }

        setIgvBrowser(browser);
        return browser;
    };

    /**
     * Debounced resize: calls visibilityChange() when the div’s size changes.
     */
    const handleResize = () => {
        requestAnimationFrame(() => {
            if (!igvRef.current || !igvBrowserRef.current) return;
            const browser = igvBrowserRef.current;

            if (!browser.trackViews || browser.trackViews.length === 0) return;

            const { offsetWidth: width, offsetHeight: height } = igvRef.current;
            const lastSize = lastSizeRef.current;
            if (lastSize && lastSize.width === width && lastSize.height === height) {
                return;
            }
            lastSizeRef.current = { width, height };

            try {
                const realIGV = (igvModuleRef.current as any)?.default
                    ? (igvModuleRef.current as any).default
                    : igvModuleRef.current;
                realIGV?.visibilityChange();
            } catch {
                // swallow
            }
        });
    };
    const debouncedResize = debounce(handleResize, 200);

    // ─────────────── Effect #1: mount-only ───────────────
    // Draw IGV exactly once, and attach ResizeObserver.
    const initializedRef = useRef(false);
    useEffect(() => {
        if (!igvRef.current) {
            return;
        }

        if (!initializedRef.current) {
            initializedRef.current = true;
            drawIGV(igvRef.current, config);
        }

        const resizeObserver = new ResizeObserver(() => {
            try {
                debouncedResize();
            } catch {
                // swallow
            }
        });
        resizeObserver.observe(igvRef.current);

        return () => {
            resizeObserver.disconnect();
            debouncedResize.clear?.();
        };
    }, []); // <-- runs exactly once per hook instance

    // ─────────────── Effect #2: skip-first-run “config reload” ───────────────
    // We only want to run loadSessionObject(...) when `config` truly changes
    // *after* the initial drawIGV. By using a ref, we can skip the first invocation.
    const hasReloadedRef = useRef(false);
    useEffect(() => {
        if (!igvBrowserRef.current) {
            // Browser not yet created—skip
            return;
        }
        // If this is the very first time igvBrowser becomes non-null, skip
        if (!hasReloadedRef.current) {
            hasReloadedRef.current = true;
            return;
        }
        // Otherwise, actually reload with the new config
        try {
            igvBrowserRef.current.loadSessionObject(config);
        } catch {
            logger.warn('Unable to reload IGV browser with new config');
        }
    }, [config]);

    return { igvRef, igvModule, igvBrowser, loadIGV };
};

export default useIGV;
